import { Clock } from 'lucide-react-native';
import { StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import type { SocialEarnings } from '@/types/voltr';

export type SocialEarningsCardProps = {
  earnings: SocialEarnings;
  testID?: string;
};

/**
 * "Earned so far" under the Get paid step (earnings mockup B): the total, then
 * each part on its own row. Every word and number is the server's
 * (Voltr::Earnings); a part not paid yet carries a clock, like the step's own
 * wait row.
 */
export function SocialEarningsCard({ earnings, testID }: SocialEarningsCardProps) {
  return (
    <View style={styles.card} testID={testID}>
      <View style={styles.row}>
        <Text variant="caption" color="textMuted">
          {earnings.title}
        </Text>
        <Text variant="bodyBold" color="deepGreen" align="right" testID={testID ? `${testID}-total` : undefined}>
          {earnings.total}
        </Text>
      </View>
      {earnings.lines.map((line) => (
        <View key={line.label} style={[styles.row, styles.divider]} accessible accessibilityLabel={`${line.label}: ${line.value}`}>
          <Text variant="caption" color="text" style={styles.label}>
            {line.label}
          </Text>
          <View style={styles.value}>
            {line.pending ? <Clock size={14} color={colors.textMuted} strokeWidth={2} /> : null}
            <Text variant="captionBold" color={line.pending ? 'textMuted' : 'deepGreen'}>
              {line.value}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: spacing.sm,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    minHeight: 44,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.sm,
  },
  divider: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
  label: { flexShrink: 1 },
  value: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
});
